import { useEffect, useState } from "react";
import { MailCheck, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { resendVerification, verifyEmail, AUTH_TOKEN_KEY } from "@/lib/api";

const btn = "inline-flex items-center gap-2 bg-[#8C3A2A] text-stone-50 px-6 py-3 rounded-sm font-mono-panel text-xs uppercase tracking-[0.2em] hover:bg-[#a34635] disabled:opacity-40 transition-colors";
const ghost = "inline-flex items-center border border-stone-400 text-stone-800 px-6 py-3 rounded-sm font-mono-panel text-xs uppercase tracking-[0.2em] hover:bg-stone-100 transition-colors";

// Shown to a signed-in account whose email is not yet confirmed. The backend refuses every protected address until then.
export function VerifyEmailNotice() {
  const { user, logout } = useAuth();
  const [state, setState] = useState("idle");
  const [msg, setMsg] = useState("");
  const resend = async () => {
    setState("sending");
    try {
      const r = await resendVerification();
      setMsg(r.message || "We sent a new link.");
      setState("sent");
    } catch (e) {
      const d = e?.response?.data?.detail;
      setMsg(typeof d === "string" ? d : "Couldn't resend right now. Please try again in a few minutes.");
      setState("error");
    }
  };
  return (
    <div data-testid="verify-email-notice" className="min-h-screen flex flex-col items-center justify-center bg-[#faf9f6] px-6 text-center">
      <MailCheck className="h-8 w-8 text-[#8C3A2A] mb-4" />
      <p className="font-mono-panel text-[11px] uppercase tracking-[0.22em] text-[#8C3A2A] mb-3">One more step</p>
      <h1 className="font-serif-display text-3xl sm:text-4xl text-stone-900 mb-3 max-w-xl leading-tight">Confirm your email address</h1>
      <p className="text-[15px] text-stone-600 max-w-md mb-2">We sent a confirmation link to <span className="font-medium text-stone-800" data-testid="verify-email-address">{user?.email}</span>. Open it to activate your account.</p>
      <p className="text-[13px] text-stone-500 max-w-md mb-8">Didn't get it? Check your spam folder, or send a new link.</p>
      <div className="flex flex-wrap gap-3 justify-center">
        <button data-testid="verify-email-resend" onClick={resend} disabled={state === "sending"} className={btn}>
          {state === "sending" ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Send a new link
        </button>
        <button data-testid="verify-email-signout" onClick={() => { logout(); window.location.href = "/"; }} className={ghost}>Sign out</button>
      </div>
      {msg && <p data-testid="verify-email-message" className={`mt-5 text-[13px] ${state === "error" ? "text-red-600" : "text-emerald-700"}`}>{msg}</p>}
    </div>
  );
}

// Landing for the emailed link (/?verify=TOKEN): confirms the address, then sends the person to the portal.
export function VerifyEmailLanding({ token }) {
  const [state, setState] = useState("working");
  const [msg, setMsg] = useState("");
  useEffect(() => {
    verifyEmail(token).then((r) => { setMsg(r.message); setState("ok"); })
      .catch((e) => { const d = e?.response?.data?.detail; setMsg(typeof d === "string" ? d : "This link didn't work."); setState("error"); });
  }, [token]);
  const signedIn = !!localStorage.getItem(AUTH_TOKEN_KEY);
  return (
    <div data-testid="verify-email-landing" className="min-h-screen flex flex-col items-center justify-center bg-[#faf9f6] px-6 text-center">
      <p className="font-mono-panel text-[11px] uppercase tracking-[0.22em] text-[#8C3A2A] mb-3">Email confirmation</p>
      <h1 className="font-serif-display text-3xl sm:text-4xl text-stone-900 mb-3 max-w-xl leading-tight">
        {state === "working" ? "Confirming…" : state === "ok" ? "Your email is confirmed" : "That link didn't work"}
      </h1>
      <p data-testid="verify-email-landing-message" className="text-[15px] text-stone-600 max-w-md mb-8">{msg}</p>
      {state !== "working" && (
        <a href="/" data-testid="verify-email-continue" className={btn}>{signedIn ? "Continue to The Writing Studio" : "Sign in"}</a>
      )}
    </div>
  );
}
